import { tokens } from "../../../theme";
import type { NavConfig } from "../../../hooks/useNavConfig";
import { RoleIndex } from "../../../models/role";

/**
 * The one prop contract both shells implement.
 *
 * NavRail and TopBar are interchangeable: <AppShell> picks by themeConfig.shell
 * and passes exactly this. Anything a single shell cannot express is accepted
 * and ignored rather than added as a shell-specific prop — `collapsed` means
 * nothing to a top bar — so a theme flip never changes a caller.
 */
export interface ShellChromeProps {
  nav: NavConfig;
  selectedItemId: string;
  onSelectItem: (id: string) => void;
  variant: "client" | "system";
  onExitSystem?: () => void;
  /** Client name: the rail's "back to" target, the top bar's context chip. */
  contextLabel?: string;
  user?: { name: string; roleLabel: string; initial: string };
  onSelectProgram?: (programIndex: number) => void;
  onOpenSystemManagement?: () => void;
  onLogout?: () => void;
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
  /** Opens the context switcher. The rail and top bar trigger it from the
   *  context bar's title; the legacy chrome uses its Portal Management button. */
  onOpenSwitcher?: (anchor: HTMLElement) => void;
}

/**
 * Text and fills that sit on the navy/dark chrome. Both shells draw on the same
 * surface, so these live here rather than being retyped as rgba literals in
 * each — the theme has no token for "white at 45% on navy".
 */
export const onChrome = {
  strong: "#fff",
  primary: "rgba(255,255,255,.9)",
  secondary: "rgba(255,255,255,.72)",
  muted: "rgba(255,255,255,.6)",
  icon: "rgba(255,255,255,.7)",
  hover: "rgba(255,255,255,.09)",
  fill: "rgba(255,255,255,.10)",
  border: "rgba(255,255,255,.16)",
  divider: "rgba(255,255,255,.12)"
} as const;

// Title Case, not sentence case. These name a role the user HAS — it reads as a
// title beside their name, like a job title would, so both words are capitalised.
// The sentence-case rule in the refresh applies to UI copy and controls, not to
// proper nouns like this.
const ROLE_LABEL: Record<number, string> = {
  [RoleIndex.Super]: "Super Admin",
  [RoleIndex.Admin]: "Admin",
  [RoleIndex.AccountAdmin]: "Account Admin",
  [RoleIndex.ClientUser]: "Client User"
};

/** Shapes the store's user for any chrome. One copy so the shells agree. */
export const toShellUser = (user: any) => ({
  name: [user?.first_name, user?.last_name].filter(Boolean).join(" "),
  roleLabel: ROLE_LABEL[user?.role_index] ?? "",
  initial: user?.first_name?.charAt(0)?.toUpperCase() ?? ""
});

/** Logo height in the chrome. One constant so both shells stay in step. */
export const LOGO_HEIGHT = 42;

/** Ghost control on the dark chrome — the top bar's secondary actions. */
export const chromeButtonSx = {
  color: onChrome.primary,
  borderRadius: `${tokens.radius.control}px`,
  "&:hover": { bgcolor: onChrome.hover }
} as const;

/**
 * Log out is the one green control in the app.
 *
 * positive.text (#2E7D32), never positive.main: white on #38B54A is 2.3:1 and
 * invariant 5 forbids a mark that carries meaning failing AA. This clears it at
 * 5.1:1. Hover flips to the brighter green with dark text, which also passes.
 */
export const logoutButtonSx = {
  minWidth: 0,
  flex: "none",
  fontSize: tokens.typography.caption,
  borderRadius: `${tokens.radius.control}px`,
  bgcolor: tokens.color.positive.text,
  color: onChrome.strong,
  "&:hover": { bgcolor: tokens.color.positive.main, color: tokens.color.ink }
} as const;
